import React, { useMemo } from "react";

import { Autocomplete, Box, TextField, Typography } from "@mui/material";
import { MaterialReactTable, type MRT_ColumnDef } from "material-react-table";
import { ConsolidationMapping } from "store/types";

interface MappingTableProps {
  itemType: "Measure" | "Asset Class";
  mappings: ConsolidationMapping[];
  items: Array<{ id: number; title?: string; name?: string }>;
  onMappingChange: (name: string, bankName: string, value: number | null) => void;
}

export const MappingTable: React.FC<MappingTableProps> = ({ itemType, mappings, items, onMappingChange }) => {
  const columns = useMemo<MRT_ColumnDef<ConsolidationMapping>[]>(
    () => [
      {
        accessorKey: "consolidatedName",
        header: `Original ${itemType} Name`,
        size: 200
      },
      {
        accessorKey: "itemId",
        header: `Map to ${itemType}`,
        size: 200,
        Cell: ({ row }) => (
          <Autocomplete
            options={items || []}
            getOptionLabel={(option) => option.title ?? option.name ?? ""}
            defaultValue={items?.find((item) => item.id === row.original.itemId) || null}
            onChange={(_, newValue) =>
              onMappingChange(row.original.consolidatedName, row.original.bankName, newValue?.id || null)
            }
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                size="small"
                placeholder={`Select a ${itemType.toLowerCase()}`}
              />
            )}
          />
        )
      },
      {
        accessorKey: "bankName",
        header: "Bank Name",
        size: 200
      }
    ],
    [items, onMappingChange]
  );

  return (
    <MaterialReactTable
      columns={columns}
      data={mappings}
      enableStickyHeader
      enableColumnFilters={false}
      enableColumnActions={false}
      enableColumnDragging={false}
      enableHiding={false}
      enableDensityToggle={false}
      enableFullScreenToggle={false}
      muiTablePaperProps={{ sx: { display: "flex", flexDirection: "column", height: "100%" } }}
      initialState={{ pagination: { pageSize: 10, pageIndex: 0 } }}
      enablePagination
      enableBottomToolbar
      renderEmptyRowsFallback={() => (
        <Box sx={{ textAlign: "center", py: 6 }}>
          <Typography variant="h6" gutterBottom>
            No Measure Consolidations
          </Typography>
          <Typography variant="body1" color="text.secondary">
            There are no measure consolidations to display yet.
          </Typography>
        </Box>
      )}
    />
  );
};
